import { supabase } from "../admin/supabase";

/**
 * The `media` storage bucket is private, so the "public" URLs produced by
 * supabase.storage.getPublicUrl() don't resolve. Anyone may still read the
 * files (the "Public read media" policy), but the URL must be a short-lived
 * signed URL. These helpers detect stored media URLs and exchange them for
 * signed ones at render time — documents and photos stay replaceable from the
 * admin CMS without making the bucket public.
 */

const PUBLIC_MARKER = "/storage/v1/object/public/media/";
const SIGN_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days

/** Extract the object path inside the `media` bucket from a stored URL. */
export function storagePathFromUrl(url) {
    if (!url) return null;
    const s = String(url);
    const i = s.indexOf(PUBLIC_MARKER);
    if (i === -1) return null;
    return s.slice(i + PUBLIC_MARKER.length).split("?")[0];
}

/** True when the URL points at an object inside the private `media` bucket. */
export function isStoredMediaUrl(url) {
    return Boolean(storagePathFromUrl(url));
}

/**
 * Resolve a stored document URL to something a visitor can open:
 * media-bucket URLs become signed URLs (1h), everything else passes through.
 */
export async function resolveDocumentUrl(url) {
    const path = storagePathFromUrl(url);
    if (!path) return url || null;
    try {
        const { data, error } = await supabase.storage
            .from("media")
            .createSignedUrl(path, 60 * 60);
        if (error || !data?.signedUrl) return url;
        return data.signedUrl;
    } catch {
        return url;
    }
}

/* ------------------------------------------------------------------ */
/* Image resolution (cached, so a page full of photos signs each once) */
/* ------------------------------------------------------------------ */

const cache = new Map(); // path -> signed url
const inflight = new Map(); // path -> promise

/** Signed URL for a stored image, cached for the life of the page. */
export async function resolveImageUrl(url) {
    const path = storagePathFromUrl(url);
    if (!path) return url || null;
    if (cache.has(path)) return cache.get(path);
    if (inflight.has(path)) return inflight.get(path);

    const promise = (async () => {
        try {
            const { data, error } = await supabase.storage
                .from("media")
                .createSignedUrl(path, SIGN_TTL_SECONDS);
            const signed = error ? null : data?.signedUrl;
            if (signed) cache.set(path, signed);
            return signed || url;
        } catch {
            return url;
        } finally {
            inflight.delete(path);
        }
    })();

    inflight.set(path, promise);
    return promise;
}

/** Synchronous read of an already-resolved image URL (or null). */
export function cachedImageUrl(url) {
    const path = storagePathFromUrl(url);
    if (!path) return null;
    return cache.get(path) || null;
}
